/* eslint-disable jsx-a11y/alt-text */
import React, { useCallback, useState } from "react";
import Table from "Components/Common/Table/Table";
import { TableHeading } from "../TableHeading/TableHeading";
import { Toggle } from "Components/Common/ToggleBtn/Toggle";
import { useStateAccessLevels } from "CustomHooks";
import { Button } from "Components/Common/Button/Button";
import { useBulkUpdateStates } from "CustomHooks/useBulkUpdateStates";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import { VisitTypesCell } from "./VisitTypesCell";
const HEADINGS = ["Doctor", "States", "Notes", "Skip Chat", "Visit Types", "Enabled"];

export const StatesTable = ({ searchedProvider }) => {
  const { data, isLoading, refetch, hasNext, hasPrevious, goToNextPage, goToPreviousPage } = useStateAccessLevels(5, searchedProvider);
  const providers = data?.result?.result?.data || [];
  const [editStatus, setEditStatus] = useState(false);
  // Track changes per doctor/state
  const [updatedProviders, setUpdatedProviders] = useState({});

  const { mutate: saveChanges, isLoading: saving } = useBulkUpdateStates();
  // --- Toggle Handler ---
  const handleToggle = (doctorId, stateId, key) => {
    setUpdatedProviders((prev) => {
      const doctor = prev[doctorId] || { doctorId, states: {} };
      const currentState = doctor.states[stateId] || {};
      const updatedState = {
        ...currentState,
        stateId,
        [key]: !currentState[key],
      };

      return {
        ...prev,
        [doctorId]: {
          doctorId,
          states: {
            ...doctor.states,
            [stateId]: updatedState,
          },
        },
      };
    });
  };
  // Non-boolean fields (enabledVisitTypes array/null) set the value directly
  const handleValueChange = (doctorId, stateId, key, value) => {
    setUpdatedProviders((prev) => {
      const doctor = prev[doctorId] || { doctorId, states: {} };
      const currentState = doctor.states[stateId] || {};
      return {
        ...prev,
        [doctorId]: {
          doctorId,
          states: {
            ...doctor.states,
            [stateId]: { ...currentState, stateId, [key]: value },
          },
        },
      };
    });
  };
  const onCancelPress = useCallback(() => {
    setUpdatedProviders({});
    setEditStatus(false);
  }, []);
  const onSavePress = () => {
    const payload = Object.values(updatedProviders).map((provider) => {
      return {
        doctorId: provider.doctorId,
        states: Object.values(provider.states),
      };
    });
    if (payload.length === 0) {
      setEditStatus(false);
      return;
    }
    saveChanges(payload, {
      onSuccess: async () => {
        await refetch();
        setUpdatedProviders({});
        setEditStatus(false);
      },
      onError: (err) => {
        console.error("Update failed", err);
      },
    });
  };

  if (isLoading || saving)
    return (
      <div className="flex items-center justify-center h-64 ">
        <div className={`w-${10} h-${10} border-l-2 border-b-2 border-gray-400 rounded-full animate-spin`} />
      </div>
    );
  return (
    <>
      <div className="flex justify-end w-full mb-2 pr-4 py-3 gap-3">
        <Button
          variant="Secondary"
          className="w-20 font-normal text-sm py-1 px-6 rounded-md"
          btnText="Cancel"
          isDisabled={!editStatus}
          onClick={onCancelPress}
        />
        <Button
          className="w-20 font-normal text-sm py-1 px-6 rounded-md text-white"
          btnText={editStatus ? (saving ? "Saving..." : "Save") : "Edit"}
          isDisabled={saving}
          onClick={() => (editStatus ? onSavePress() : setEditStatus(true))}
        />
      </div>

      <Table
        heading={<TableHeading TableHeader={HEADINGS} />}
        body={providers.map((provider) => (
          <React.Fragment key={provider._id}>
            {provider?.states?.map((state, idx) => {
              // Show updated values from updatedProviders if exist
              const updatedDoctor = updatedProviders[provider._id];

              const updatedState = updatedDoctor?.states[state.stateId];

              const notesValue = updatedState?.enableNotes ?? state.enableNotes;
              const skipChatValue = updatedState?.enableSkipChat ?? state.enableSkipChat;
              const visitTypesValue =
                updatedState?.enabledVisitTypes !== undefined ? updatedState.enabledVisitTypes : state.enabledVisitTypes ?? null;
              const enabledValue = updatedState?.enabled ?? state.enabled;
              return (
                <tr key={state.stateId} className="border-b hover:bg-gray-50">
                  {/* Doctor cell (only for first row) */}
                  {idx === 0 && (
                    <td rowSpan={provider.states.length} className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 align-top">
                      {provider.doctorName}
                    </td>
                  )}
                  {/* State name */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{state.stateName}</td>
                  {/* Notes toggle */}
                  <td className="px-6 py-4 text-center">
                    <Toggle
                      enabled={notesValue}
                      disabled={!enabledValue || !editStatus}
                      onChange={() => handleToggle(provider._id, state.stateId, "enableNotes")}
                    />
                  </td>
                  {/* Skip Chat toggle */}
                  <td className="px-6 py-4 text-center">
                    <Toggle
                      enabled={skipChatValue}
                      disabled={!enabledValue || !editStatus || !notesValue}
                      onChange={() => handleToggle(provider._id, state.stateId, "enableSkipChat")}
                    />
                  </td>
                  {/* Visit types this doctor may see in this state (independent of Notes) */}
                  <td className="px-6 py-4 text-center">
                    <VisitTypesCell
                      value={visitTypesValue}
                      disabled={!enabledValue || !editStatus}
                      onChange={(next) => handleValueChange(provider._id, state.stateId, "enabledVisitTypes", next)}
                    />
                  </td>
                  {/* Enabled toggle */}
                  <td className="px-6 py-4 text-center">
                    <Toggle
                      enabled={enabledValue}
                      disabled={!editStatus}
                      onChange={() => handleToggle(provider._id, state.stateId, "enabled")}
                    />
                  </td>
                </tr>
              );
            })}
          </React.Fragment>
        ))}
      />
      <div className="my-4 mx-4">
        <PaginationBtns isNextDisabled={!hasNext} isPrevDisabled={!hasPrevious} onNext={goToNextPage} onPrevious={goToPreviousPage} />
      </div>
    </>
  );
};
